import type { RefObject } from 'react';
import { useRef, useCallback, useEffect } from 'react';
import { isNotUndefined } from '@wistia/type-guards';
import {
  FOCUSABLE_ELEMENT_SELECTORS,
  createAriaHider,
  isFocusableElement,
  isTabbableElement,
  markForFocusLater,
  returnFocus,
  scopeTab,
  setupScopedFocus,
  teardownScopedFocus,
} from './helpers';

type UseFocusTrapOptions = {
  disableAriaHider?: boolean;
  focusSelector?: HTMLElement | RefObject<HTMLElement | null> | string | null | undefined;
};

const isRef = <T extends HTMLElement>(val: unknown): val is RefObject<T | null> => {
  return val !== null && typeof val === 'object' && 'current' in val;
};

export const useFocusTrap = (active = true, options: UseFocusTrapOptions = {}) => {
  const ref = useRef<HTMLElement | null>(null);
  const restoreAriaRef = useRef<(() => void) | null>(null);

  const setRef = useCallback(
    (node: HTMLElement | null | undefined) => {
      if (restoreAriaRef.current !== null) {
        restoreAriaRef.current();
      }

      if (ref.current) {
        returnFocus();
        teardownScopedFocus();
      }

      if (active && node !== null && node !== undefined) {
        setupScopedFocus(node);
        markForFocusLater();

        const processNode = (node2: HTMLElement) => {
          // check if we should disable aria
          restoreAriaRef.current = !(options.disableAriaHider ?? false)
            ? createAriaHider(node2)
            : null; // Find the initial focus element

          let focusElement: HTMLElement | null = null;
          if (isNotUndefined(options.focusSelector)) {
            if (isRef(options.focusSelector)) {
              focusElement = options.focusSelector.current;
            } else {
              focusElement =
                typeof options.focusSelector === 'string'
                  ? node2.querySelector(options.focusSelector)
                  : options.focusSelector;
            }
          }

          if (!focusElement) {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            const children = Array.from(
              node2.querySelectorAll(FOCUSABLE_ELEMENT_SELECTORS),
            ) as HTMLElement[];
            focusElement = // Prefer tabbable elements, But fallback to any focusable element
              children.find(isTabbableElement) ?? // But fallback to any focusable element
              children.find(isFocusableElement) ?? // Nothing found
              null; // If everything else fails, see if the node itself can handle focus

            if (!focusElement && isFocusableElement(node2)) {
              focusElement = node2;
            }
          }

          if (focusElement) {
            // Set the initial focus inside the traps
            focusElement.focus();
          }

          if (!focusElement && process.env['NODE_ENV'] === 'development') {
            // eslint-disable-next-line no-console
            console.warn(
              '[useFocusTrap]: Failed to find a focusable element after activating the focus trap. Make sure to include at an element that can recieve focus. As a fallback, you can also set "tabIndex={-1}" on the focus trap node.',
              node2,
            );
          }
        }; // Delay processing the HTML node by a frame. This ensures focus is assigned correctly.

        setTimeout(() => {
          // node.ownerDocument should always be a truthy value
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/strict-boolean-expressions
          if (node.ownerDocument) {
            processNode(node);
          }

          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/strict-boolean-expressions
          if (!node.ownerDocument && process.env['NODE_ENV'] === 'development') {
            // eslint-disable-next-line no-console
            console.warn(
              '[useFocusTrap]: The focus trap is not part of the DOM yet, so it is unable to correctly set focus. Make sure to render the ref node.',
              node,
            );
          }
        });

        ref.current = node;
      } else {
        ref.current = null;
      }
    },
    [active, options.focusSelector, options.disableAriaHider],
  );

  useEffect(() => {
    if (!active) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab' && ref.current) {
        scopeTab(ref.current, event);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [active]);

  return setRef;
};
